import i18next from "i18next";
import { Content, TDocumentDefinitions } from "pdfmake/interfaces";
import { TFunction } from "react-i18next";
import FormatDate from "go-core/components/Formatters/FormatDate";
import FormatMoney from "go-core/components/Formatters/FormatMoney";
import { CustomFieldEntityApi, CustomFieldTemplateApi } from "go-segment/services/types";
import {
	OrderApi,
	OrderItemApi,
	PurchaseApi,
	PurchaseItemApi,
} from "../../../../../../../../services/Api/Organization/types";
import { A4WIDTH, DOCUMENT_PDF_TABLE_STYLES } from "./consts";
import { generateDocumentItemsTable } from "./documentTable";
import { DocumentPdfEntity, ExportDocumentPdfConfig } from "./types";

export const exportDocumentPdf = async (
	config: ExportDocumentPdfConfig,
	entity: DocumentPdfEntity,
	t: TFunction,
	data: (PurchaseItemApi | OrderItemApi)[],
	document: PurchaseApi | OrderApi,
	customFieldsConfig: CustomFieldTemplateApi[],
	additionalDate?: string,
	print?: boolean
) => {
	const table = generateDocumentItemsTable(config, entity, t, data, document);
	let organizationName: string | undefined = undefined;
	let companyName: string | undefined = undefined;
	let taxIdNo: string | undefined = undefined;
	const title = `${
		entity === DocumentPdfEntity.PURCHASE
			? t("enums.custom_fields.resources.PURCHASE_ORDER")
			: t("enums.custom_fields.resources.SALES_ORDER")
	} ${document.document_number}`;

	if ((config?.company && config.company !== "undefined") || (config?.taxIdNo && config.taxIdNo !== "undefined")) {
		if (config?.company && config.company !== "undefined") companyName = config.company;
		if (config?.taxIdNo && config.taxIdNo !== "undefined") taxIdNo = config.taxIdNo;
	} else if (config?.organization && config.organization !== "undefined") {
		organizationName = config?.organization;
	}

	const docDefinition: TDocumentDefinitions = {
		pageOrientation: table.pageOrientation,
		pageMargins: [20, 20, 20, 40],
		footer(currentPage, pageCount) {
			const date = new Date();
			const utc = `${date.toLocaleDateString()} ${date.toLocaleTimeString()}`;
			let footerFirstColumnText = "";

			if (organizationName) footerFirstColumnText += `${i18next.t("lib:common.word.venue")}: ${organizationName}`;
			if (companyName) footerFirstColumnText += `\n ${companyName}`;
			if (taxIdNo) footerFirstColumnText += `\n ${i18next.t("lib:common.word.tax_id_no.title")}: ${taxIdNo}`;
			footerFirstColumnText += `\n ${utc} by GoPOS.pl`;

			return {
				columns: [
					{
						text: `${footerFirstColumnText}`,
						alignment: "left",
						fontSize: 8,
						margin: [20, organizationName ? 10 : 0, 0, 0],
					},
					{
						text: `${i18next.t("lib:common.word.page")} ${currentPage.toString()} ${i18next.t(
							"lib:common.word.of"
						)} ${pageCount}`,
						fontSize: 8,
						alignment: "right",
						margin: [0, 20, 20, 0],
					},
				],
			};
		},
		content: [
			...getRightCornerInfoOnTop(t, document, entity),
			{
				canvas: [{ type: "line", x1: 0, y1: 0, x2: A4WIDTH - 2 * 20, y2: 0, lineWidth: 1 }],
				margin: [0, 10, 0, 10],
			},
			{
				text: title,
				style: "header",
			},
			{
				canvas: [{ type: "line", x1: 0, y1: 0, x2: A4WIDTH - 2 * 20, y2: 0, lineWidth: 1 }],
				margin: [0, 10, 0, 30],
			},
			...getInformationAboveTable(document, entity, t, customFieldsConfig, config, additionalDate),
			{
				style: "table",
				color: "#444",
				table,
			},
			...getPricesContent(document, entity, t, !!config.printPrices),
		],
		styles: DOCUMENT_PDF_TABLE_STYLES,
	};

	const pdfMake = (await import("pdfmake/build/pdfmake")).default;
	const pdfFonts = (await import("pdfmake/build/vfs_fonts")).default;
	pdfMake.vfs = pdfFonts.pdfMake?.vfs ?? pdfFonts;

	if (print) pdfMake.createPdf(docDefinition).print();
	else pdfMake.createPdf(docDefinition).download(`${title}.pdf`);
};

const getRightCornerInfoOnTop = (
	t: TFunction,
	document: PurchaseApi | OrderApi,
	entity: DocumentPdfEntity
): Array<Content> => {
	let dateTitle = `${
		entity === DocumentPdfEntity.ORDER
			? t("modules.order.field.order_date.title")
			: t("modules.purchase.field.purchase_date.title")
	}: `;

	if ("ordered_at" in document) {
		dateTitle += FormatDate(document.ordered_at);
	} else if ("purchased_at" in document) {
		dateTitle += FormatDate(document.purchased_at);
	}

	return [
		{
			text: dateTitle,
			style: "rightCorner",
		},
		document.reference_number && {
			text: `${t("common.word.reference_number")}: ${document.reference_number}`,
			style: "rightCorner",
		},
	];
};

const getInformationAboveTable = (
	document: OrderApi | PurchaseApi,
	entity: DocumentPdfEntity,
	t: TFunction,
	customFieldsConfig: CustomFieldTemplateApi[],
	config: ExportDocumentPdfConfig,
	additionalDate?: string
): Array<Content> => {
	return [
		{
			columns: [
				{
					width: "*",
					text:
						entity === DocumentPdfEntity.ORDER
							? t("modules.order.field.purchaser.title")
							: t("modules.purchase.field.provider.title"),
					style: "label",
				},
				{
					width: "auto",
					text:
						entity === DocumentPdfEntity.ORDER
							? t("common.word.stock")
							: t("modules.purchase.field.delivery_to.title"),
					style: "label",
					alignment: "right",
				},
			],
		},
		{
			columns: [
				{
					width: "*",
					text: document.contact?.name ?? "-",
					style: "value",
				},
				{
					width: "auto",
					text: document.stock?.name ?? "-",
					style: "value",
					alignment: "right",
				},
			],
		},
		{
			text: "",
			style: "separator",
		},
		{
			columns: [
				{
					width: "*",
					text: t("common.word.comment.title"),
					style: "label",
				},
				{
					width: "auto",
					text: document.custom_fields[0]
						? findCustomFieldConfig(
								customFieldsConfig,
								document.custom_fields[0],
								!!config.printCustomFields
						  )?.name ?? ""
						: "",
					style: "label",
					alignment: "right",
				},
			],
		},
		{
			columns: [
				{
					width: "*",
					text: document.comment ?? "-",
					style: "value",
				},
				{
					width: "auto",
					text: !config.printCustomFields
						? ""
						: document.custom_fields[0]
						? document.custom_fields[0].value
						: "",
					style: "value",
					alignment: "right",
				},
			],
		},
		{
			text: "",
			style: "separator",
		},
		{
			columns: [
				{
					width: "*",
					text: additionalDate
						? entity === DocumentPdfEntity.ORDER
							? t("modules.order.field.release_date.title")
							: t("modules.purchase.field.acceptance_date.title")
						: "",
					style: "label",
				},
				{
					width: "auto",
					text: document.custom_fields[1]
						? findCustomFieldConfig(
								customFieldsConfig,
								document.custom_fields[1],
								!!config.printCustomFields
						  )?.name ?? ""
						: "",
					style: "label",
					alignment: "right",
				},
			],
		},
		{
			columns: [
				{
					width: "*",
					text: additionalDate ? FormatDate(additionalDate) : "",
					style: "value",
				},
				{
					width: "auto",
					text: !config.printCustomFields
						? ""
						: document.custom_fields[1]
						? document.custom_fields[1].value
						: "",
					style: "value",
					alignment: "right",
				},
			],
		},
		...getRestOfCustomFields(customFieldsConfig, document.custom_fields, !!config.printCustomFields),
	];
};

const findCustomFieldConfig = (
	customFieldsConfig: CustomFieldTemplateApi[],
	customField: CustomFieldEntityApi,
	print: boolean
) => {
	if (!print) return undefined;

	return customFieldsConfig.find((config) => config.slug === customField.slug);
};

const getRestOfCustomFields = (
	customFieldsConfig: CustomFieldTemplateApi[],
	customFields: CustomFieldEntityApi[],
	print: boolean
): Array<Content> => {
	const restOfCustomFields = customFields.filter((_, index) => index > 1);
	const content: Content[] = [];

	if (!print) return content;

	if (restOfCustomFields.length > 0) {
		restOfCustomFields.forEach((customField) => {
			content.push(
				{
					text: "",
					style: "separator",
				},
				{
					columns: [
						{
							width: "*",
							text: "",
							style: "label",
						},
						{
							width: "auto",
							text: findCustomFieldConfig(customFieldsConfig, customField, print)?.name ?? "",
							style: "label",
							alignment: "right",
						},
					],
				},
				{
					columns: [
						{
							width: "*",
							text: "",
							style: "value",
						},
						{
							width: "auto",
							text: customField.value ?? "",
							style: "value",
							alignment: "right",
						},
					],
				}
			);
		});
	}

	return content;
};

const getPricesContent = (
	document: PurchaseApi | OrderApi,
	entity: DocumentPdfEntity,
	t: TFunction,
	show: boolean
): Array<Content> => {
	if (!show) return [];

	if (entity === DocumentPdfEntity.ORDER) {
		const parsedDocument = document as OrderApi;
		return [
			{
				text: `${t("modules.order.field.net_sales_amount.title")}: ${FormatMoney(
					parsedDocument.total_price_net_money
				)}`,
				style: ["pricesTop", "prices"],
			},
			{
				text: `${t("modules.order.field.gross_sales_amount.title")}: ${FormatMoney(
					parsedDocument.total_price_gross_money
				)}`,
				style: "prices",
			},
			{
				text: `${t("modules.order.field.sales_tax.title")}: ${FormatMoney(
					parsedDocument.total_tax_gross_money
				)}`,
				style: "prices",
			},
			{
				text: `${t("common.word.cost")}: ${FormatMoney(parsedDocument.total_fifo_cost_net_money)}`,
				style: "prices",
			},
		];
	}
	if (entity === DocumentPdfEntity.PURCHASE) {
		const parsedDocument = document as PurchaseApi;
		return [
			{
				text: `${t("modules.purchase.field.net_amount.title")}: ${FormatMoney(
					parsedDocument.total_price_net_money
				)}`,
				style: ["pricesTop", "prices"],
			},
			{
				text: `${t("common.word.tax")}: ${FormatMoney(
					parsedDocument.tax_gross
						? parsedDocument.total_tax_gross_money
						: parsedDocument.total_tax_price_money
				)}`,
				style: "prices",
			},
			{
				text: `${t("modules.purchase.field.gross_amount.title")}: ${FormatMoney(
					parsedDocument.total_price_gross_money
				)}`,
				style: "prices",
			},
		];
	}

	return [];
};
